// ============================================================
// Chess Arena — Procedural Level Generator (Levels 13+)
// GDD Section 3.4
// ============================================================

import { LevelConfig, PieceType, SpawnWeights } from '../core/types';

// --- Default opponent pool for procedural levels ---

interface OpponentEntry {
  name: string;
  portrait: string;
}

const DEFAULT_OPPONENT_POOL: OpponentEntry[] = [
  { name: 'Sir Galahad',      portrait: '🗡️' },
  { name: 'Lady Morgana',     portrait: '🔮' },
  { name: 'Baron Ironclad',   portrait: '🏰' },
  { name: 'The Dark Bishop',  portrait: '🌑' },
  { name: 'Queen Seraphina',  portrait: '👑' },
  { name: 'Lord Blackwood',   portrait: '🖤' },
  { name: 'Captain Steele',   portrait: '⚔️' },
  { name: 'Master Zephyr',    portrait: '🌀' },
  { name: 'Warden Knox',      portrait: '🛡️' },
  { name: 'Archmage Theron',  portrait: '✨' },
  { name: 'Count Valoris',    portrait: '🎖️' },
  { name: 'The Iron Maiden',  portrait: '♠️' },
];

// --- Difficulty scaling constants ---

const RATING_BASE = 1250;
const RATING_PER_LEVEL = 50;

// Spawn timer decreases from 10s to 5s over levels 13–50
const SPAWN_TIMER_MAX = 10;
const SPAWN_TIMER_MIN = 5;
const SPAWN_TIMER_SCALE_START = 13;
const SPAWN_TIMER_SCALE_END = 50;

// Wave count increases from 2 to 4 over levels
const WAVE_COUNT_MIN = 2;
const WAVE_COUNT_MAX = 4;

// Terrain limits
const ROCKS_MIN = 1;
const ROCKS_MAX = 5;
const BUSHES_MIN = 0;
const BUSHES_MAX = 4;

const MATCH_TIMER_SEC = 60;

// Enemy King hidden chance after level 20
const HIDDEN_KING_START_LEVEL = 20;

/**
 * Generate a procedural level config for levels 13+.
 */
export function generateLevel(levelNumber: number): LevelConfig {
  if (levelNumber < 13) {
    console.warn(`[LevelGenerator] Level ${levelNumber} should use hand-tuned config; generating procedurally as fallback.`);
  }

  const difficulty = levelNumber - 12; // 1-based difficulty offset

  // --- Opponent ---
  const opponent = pickRandom(DEFAULT_OPPONENT_POOL);
  const rating = RATING_BASE + difficulty * RATING_PER_LEVEL;

  // --- Spawn timers ---
  const t = clamp01((levelNumber - SPAWN_TIMER_SCALE_START) / (SPAWN_TIMER_SCALE_END - SPAWN_TIMER_SCALE_START));
  const spawnTimer = lerp(SPAWN_TIMER_MAX, SPAWN_TIMER_MIN, t);
  const playerSpawnTimer = Math.max(spawnTimer + 1, 4); // player gets slightly more time

  // --- Spawn weights (shift toward heavier pieces at higher levels) ---
  const enemyWeights = computeSpawnWeights(difficulty);
  const playerWeights = computePlayerSpawnWeights();

  // --- Waves ---
  const waveCount = Math.min(WAVE_COUNT_MAX, WAVE_COUNT_MIN + Math.floor(difficulty / 4));
  const waves = generateWaves(waveCount, difficulty);

  // --- Armies ---
  const playerArmy = computePlayerArmy(difficulty);
  const enemyArmy = computeEnemyArmy(difficulty);

  // --- Terrain ---
  const maxRocks = Math.min(ROCKS_MAX, ROCKS_MIN + Math.floor(difficulty / 5));
  const maxBushes = Math.min(BUSHES_MAX, BUSHES_MIN + Math.floor(difficulty / 6));

  // --- Enemy King visibility ---
  const enemyKingVisible = levelNumber < HIDDEN_KING_START_LEVEL
    ? true
    : Math.random() > 0.4; // 40% chance to be hidden after level 20

  const enemyKingAppearsAfterWave = enemyKingVisible
    ? null
    : Math.min(waveCount, 1 + Math.floor(Math.random() * 2));

  return {
    level: levelNumber,
    opponent: {
      name: opponent.name,
      rating,
      portrait: opponent.portrait,
    },
    playerArmy,
    enemyArmy,
    enemyKingVisible,
    enemyKingAppearsAfterWave,
    playerSpawn: {
      timerSeconds: roundTo1(playerSpawnTimer),
      weights: playerWeights,
    },
    enemySpawn: {
      timerSeconds: roundTo1(spawnTimer),
      weights: enemyWeights,
    },
    waves,
    terrain: {
      maxRocks,
      maxBushes,
    },
    matchTimerSec: MATCH_TIMER_SEC,
    introTips: [],
    tutorialLevel: false,
    newMechanic: null,
  };
}

// --- Helpers ---

function computeSpawnWeights(difficulty: number): SpawnWeights {
  // Gradually shift from Pawns/Knights toward Rooks/Queens
  const t = clamp01(difficulty / 38); // 0 at level 13, 1 at level 50

  return {
    Pawn:   Math.round(lerp(50, 20, t)),
    Knight: Math.round(lerp(30, 20, t)),
    Bishop: Math.round(lerp(15, 20, t)),
    Rook:   Math.round(lerp(5, 25, t)),
    Queen:  Math.round(lerp(0, 15, t)),
  };
}

function computePlayerSpawnWeights(): SpawnWeights {
  return {
    Pawn:   40,
    Knight: 25,
    Bishop: 20,
    Rook:   10,
    Queen:  5,
  };
}

function computePlayerArmy(difficulty: number): Partial<Record<PieceType, number>> {
  // Player always starts lean: King + 1 piece, relying on spawns to grow
  if (difficulty >= 5) {
    return { King: 1, Queen: 1 };
  }
  return { King: 1, Knight: 1 };
}

function computeEnemyArmy(difficulty: number): Partial<Record<PieceType, number>> {
  // Enemy starts small too — waves and spawns provide reinforcements
  const army: Partial<Record<PieceType, number>> = {
    King: 1,
    Pawn: 2,
  };

  if (difficulty >= 3) army.Knight = 1;
  if (difficulty >= 8) army.Rook = 1;
  if (difficulty >= 15) army.Bishop = 1;

  return army;
}

function generateWaves(
  waveCount: number,
  difficulty: number
): { triggerTimeSec: number; pieces: Partial<Record<PieceType, number>> }[] {
  const waves: { triggerTimeSec: number; pieces: Partial<Record<PieceType, number>> }[] = [];
  const t = clamp01(difficulty / 38);

  for (let i = 0; i < waveCount; i++) {
    // Waves arrive evenly spaced across the match timer
    const triggerTime = Math.round(((i + 1) / (waveCount + 1)) * MATCH_TIMER_SEC);

    // Wave composition scales with difficulty (kept lean — AI is smarter now)
    const pieces: Partial<Record<PieceType, number>> = {};
    const totalPieces = 1 + Math.floor(difficulty / 12);

    // Distribute pieces across types based on difficulty
    if (totalPieces >= 1) pieces.Pawn = Math.max(1, Math.round(totalPieces * lerp(0.6, 0.3, t)));
    if (totalPieces >= 2 && difficulty >= 1) pieces.Knight = Math.max(0, Math.round(totalPieces * lerp(0.3, 0.2, t)));
    if (difficulty >= 3) pieces.Bishop = Math.max(0, Math.round(totalPieces * lerp(0.1, 0.15, t)));
    if (difficulty >= 5) pieces.Rook = Math.max(0, Math.round(totalPieces * lerp(0, 0.2, t)));
    if (difficulty >= 10) pieces.Queen = Math.max(0, Math.round(totalPieces * lerp(0, 0.15, t)));

    // Ensure at least 1 piece in each wave
    if (Object.values(pieces).reduce((s, n) => s + (n ?? 0), 0) === 0) {
      pieces.Pawn = 1;
    }

    waves.push({ triggerTimeSec: triggerTime, pieces });
  }

  return waves;
}

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function roundTo1(n: number): number {
  return Math.round(n * 10) / 10;
}

export default generateLevel;
